import React, { useState, useEffect } from "react";
import {
  Datagrid,
  List,
  NumberField,
  TextField,
  useListContext,
  useListController,
} from "react-admin";
import {
  Button,
  Box,
  TextField as MuiTextField,
  useTheme,
  useMediaQuery,
  Autocomplete,
} from "@mui/material";

import CustomPagination from "../../Components/CustomPagination";
import { fetchAccountSummaries, fetchUserTransactions } from "../../Utilities/dashboardSummary";

const CustomFilter = () => {
  const today = new Date().toISOString().split("T")[0];
  const oneMonthAgo = new Date();
  oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);
  const defaultStartDate = oneMonthAgo.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(today);
  const [usernameOptions, setUsernameOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filter,setFilters]= useState();
  useEffect(() => {
    fetchUsernameOptions();
    handleApply(); // initial
  }, []);

  const fetchUsernameOptions = async () => {
    try {
      const res = await fetchAccountSummaries({
        page: 1,
        perPage: 50,
      });
      const usernames = res.data.map((u) => ({
        id: u.id,
        username: u.userName || "",
        roleName: u.roleName || "",
      }));
      setUsernameOptions(usernames);
    } catch (err) {
      console.error("Failed to fetch usernames:", err);
    }
  };

  const handleApply = async () => {
    const filters = {
      startDate,
      endDate,
    };

    if (selectedUser?.id) {
      filters.userId = selectedUser.id;

      // Fetch transactions and inject into filter context
      try {
        const txnResult = await fetchUserTransactions({
          userId: selectedUser.id,
          startDate,
          endDate,
        });

        filters.totalRecharge = txnResult.totalRecharge;
        filters.totalRedeem = txnResult.totalRedeem;
      } catch (err) {
        console.error("Failed to fetch user transactions:", err);
      }
    }

    setFilters(filters, {});
  };

  const handleClear = () => {
    setStartDate("");
    setEndDate("");
    setSelectedUser(null);
    setFilters({}, {});
  };

  return (
    <Box display={{ xs: "block", width: "100%", sm: "flex" }} alignItems="center" gap={2}>
      <Box display="flex" width="max-content" gap={1}>
        <MuiTextField
          size="small"
          type="date"
          label="Start Date"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => {
            const newStart = e.target.value;
            setStartDate(newStart);
            if (endDate && newStart > endDate) setEndDate("");
          }}
          inputProps={{ max: today }}
        />

        <MuiTextField
          size="small"
          type="date"
          label="End Date"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          inputProps={{ min: startDate || undefined, max: today }}
        />

        <Autocomplete
          size="small"
          sx={{ minWidth: 200 }}
          options={usernameOptions}
          getOptionLabel={(option) =>
            option.username ? `${option.username} (${option.roleName || "Unknown"})` : ""
          }
          isOptionEqualToValue={(opt, val) => opt.username === val.username}
          value={selectedUser}
          onChange={(e, newVal) => {
            setSelectedUser(newVal);
            setTimeout(handleApply, 0);
          }}
          renderInput={(params) => (
            <MuiTextField {...params} label="Search Username" variant="outlined" />
          )}
        />
      </Box>

      <Box display="flex" width="max-content">
        <Button
          variant="contained"
          sx={{
            m: 1,
            background: "#FFC107",
            color: "#0A0A0A",
            textTransform: "none",
            "&:hover": { backgroundColor: "#FFA000" },
          }}
          onClick={handleApply}
        >
          Apply
        </Button>
        <Button
          variant="outlined"
          sx={{
            m: 1,
            outline: "1px solid #FFC107",
            color: "white",
            textTransform: "none",
            border: "none",
          }}
          onClick={handleClear}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
};

export const AgentAccountList = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  
    return (
      <List
        resource="agentAccount"
        filters={<CustomFilter />}
        pagination={<CustomPagination />}
        perPage={10}
        exporter={false}
        hasCreate={false}
        hasEdit={false}
        hasShow={false}
        title="Agent Account Summary"
      >
        <Box display="flex" flexDirection="column" height="calc(100vh - 80px)">
          <Box flex={1} overflowY="auto" overflowX="hidden" pt={2}>
            <Box
              mt={4}
              bgcolor="#242424"
              borderRadius={2}
              boxShadow="none !important"
              p={isSmallScreen ? 0 : 2}
            >
              {/* You can optionally add summary UI here */}
            </Box>
          </Box>
        </Box>
      </List>
    );
  };
